import apiConfig from '../config/apiConfig';

export interface CodeSource {
  videoId: string;
  title: string;
  url: string;
  publishedAt?: string;
  thumbnailUrl?: string;
}

/**
 * Lowercase platform identifier for a creator code. Mirrors the
 * ``codes.platform`` column on the backend (Sprint 9 / Track 3).
 *
 * Preserve the stored platform when requesting similar brands so YouTube
 * and Instagram attribution stays attached to the right account.
 */
export type CodePlatform = 'youtube' | 'instagram' | 'tiktok' | 'twitch' | 'other';

export interface Code {
  id?: string;
  is_demo?: boolean;
  code: string;
  creator: string;
  creatorProfilePic?: string;
  channelHandle?: string;
  store: string;
  category: string;
  brand: string;
  discount: string;
  expirationDate?: string;
  description?: string;
  usedCount?: number;
  isFollowed?: boolean;
  supportLevel?: string;
  logo?: string;
  /**
   * Platform the code was sourced from. Defaults to ``'youtube'`` for
   * forward-compat — older API responses that pre-date the NOT NULL
   * migration may omit this, and we want the YouTube-only legacy
   * behaviour to keep working without a manual backfill on the client.
   */
  platform?: CodePlatform;
  opportunityType?: string;
  sources: CodeSource[];
  timeScraped?: string;
  computedExpiration?: string;
  creatorProfileColor?: string;
  iconUrl?: string;
  brandColor?: string;
}

export interface MergeResult { created: number; skipped: number; }

export interface SearchResult {
  query: string;
  total_results: number;
  results: Code[];
}

export interface CreatorAnalysisHistory {
  handle: string;
  has_data: boolean;
  total_codes: number;
  creators_found: string[];
  existing_codes: Code[];
}

const codeService = {
  fetchCodes: async (filters?: {
    authToken?: string;
    category?: string;
    brand?: string;
    followedOnly?: boolean;
    platform?: string;
    /** When set with the connected creator's YouTube handle, limits results to that channel's scraped codes */
    channelHandle?: string;
    /** Fallback match against code.creator when the channel has no custom URL yet */
    creatorDisplayName?: string;
  }): Promise<Code[]> => {
    let url = `${apiConfig.baseUrl}${apiConfig.endpoints.codes}`;
    
    // Add query parameters for filters
    const params = new URLSearchParams();
    
    // IMPORTANT: Show all codes including expired ones for now
    // Backend defaults to hide_expired=true, we need to override
    params.append('hide_expired', 'false');
    
    if (filters?.category && filters.category !== 'All') {
      params.append('category', filters.category);
    }
    if (filters?.brand && filters.brand !== 'All') {
      params.append('brand', filters.brand);
    }
    if (filters?.followedOnly) {
      params.append('followed_only', 'true');
    }
    if (filters?.platform && filters.platform !== 'All') {
      params.append('platform', filters.platform);
    }
    if (filters?.channelHandle?.trim()) {
      params.append('channel_handle', filters.channelHandle.trim().replace(/^@/, ''));
    }
    if (filters?.creatorDisplayName?.trim()) {
      params.append('creator_display_name', filters.creatorDisplayName.trim());
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const res = await fetch(url, { headers: filters?.authToken ? { Authorization: `Bearer ${filters.authToken}` } : undefined });
    if (!res.ok) {
      console.error('Failed to fetch codes:', res.status, res.statusText);
      throw new Error('Failed to fetch codes');
    }
    return res.json();
  },
  
  searchCodes: async (
    query: string, 
    limit: number = 50,
    filters?: {
      category?: string;
      brand?: string;
      followedOnly?: boolean;
      platform?: string;
    }
  ): Promise<SearchResult> => {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('limit', limit.toString());
    
    // IMPORTANT: Show all codes including expired ones for now
    params.append('hide_expired', 'false');
    
    // Add filter parameters
    if (filters?.category && filters.category !== 'All') {
      params.append('category', filters.category);
    }
    if (filters?.brand && filters.brand !== 'All') {
      params.append('brand', filters.brand);
    }
    if (filters?.followedOnly) {
      params.append('followed_only', 'true');
    }
    if (filters?.platform && filters.platform !== 'All') {
      params.append('platform', filters.platform);
    }
    
    const url = `${apiConfig.baseUrl}${apiConfig.endpoints.codes}/search?${params.toString()}`;
    const res = await fetch(url);
    if (!res.ok) {
      console.error('Failed to search codes:', res.status, res.statusText);
      throw new Error('Failed to search codes');
    }
    return res.json();
  },
  
  getCreatorAnalysisHistory: async (handle: string): Promise<CreatorAnalysisHistory> => {
    const url = `${apiConfig.baseUrl}/api/creators/${encodeURIComponent(handle)}/analysis-history`;
    console.log('Checking creator analysis history:', url);
    const res = await fetch(url);
    if (!res.ok) {
      console.error('Failed to get creator analysis history:', res.status, res.statusText);
      throw new Error('Failed to get creator analysis history');
    }
    return res.json();
  },
  
  addCodes: async (codes: Code[], token?: string): Promise<MergeResult> => {
    const url = `${apiConfig.baseUrl}${apiConfig.endpoints.codes}`;
    console.log('Posting codes to:', url, 'with', codes.length, 'codes');
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ codes })
    });
    if (!res.ok) {
      console.error('Failed to save codes:', res.status, res.statusText);
      throw new Error('Failed to save codes');
    }
    return res.json();
  }
};
export default codeService;
